import { Injectable, QueryList, NgZone, Renderer2 } from '@angular/core';
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { DataStore } from '@common/data-models/data-store/data-store';
import { AppService } from '@suite/app.service';
import { ServiceResponse } from "@common/data-models/service/service-response";
import { PlaybookSearchResults } from "@playbook/data-models/playbook-search-results/playbook-search-results";
import { SearchResults, TreeLikeFlatNode } from "@common/data-models/search-results/search-results";
import { ObjectTypes } from '@common/enums/object-types';
import { FieldDef } from "@common/data-models/service/field-def";
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { Record } from '@common/data-models/records/record';
import { CollectionRecord } from "@common/data-models/records/collection-record";
import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { AssignmentRecord, AssignmentCoverageType } from "@playbook/data-models/records/assignment-record";
import { TopicTemplateRecord } from "@playbook/data-models/records/topic-template-record";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { MultiValue } from "@common/data-models/complex-types/multi-value";
import { LookupList } from "@common/data-models/complex-types/lookup-list";
import { Address } from "@common/data-models/complex-types/address";
import { ChecklistSerializer, SpecialSerializer, LookupListSerializer, AddressSerializer, StaffIDListSerializer, DateTimeRangeSerializer } from "@common/data-models/serializers/serializers";
import { Table } from "@common/data-models/complex-types/table";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { StTopicItem } from "@common/components/topic-item/st-topic-item.component";
import { Util } from "@common/utils/util";
import { ListData } from "@common/data-models/service/list-data";
import { TopicViewStateRecord } from "@suite/common/data-models/records/topic-viewstate-record";
import { SingleViewStateRecord } from "@suite/common/data-models/records/single-viewstate-record";
import { Clipboard } from '@platform/components/clipboard/clipboard';
import { ClipboardTypes } from "@common/enums/clipboard-types";
import Handlebars from 'handlebars/dist/cjs/handlebars';

import { TopicService } from "@playbook/services/topic-service";
import { PlaybookSearchService } from "@playbook/services/playbook-search-service";
import { NavigationService } from "@common/services/navigation-service";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { LanguageSettings } from "@common/services/language-settings";
import { FieldMappingService } from "@common/services/field-mapping-service";

import { IPlaybookService } from "@playbook/interfaces/i-playbook-service"
import { SlideshowService } from "@common/services/slideshow-service";
import { GroupValueHelpers, GroupKeyComparatorHelpers, RecordComparatorHelpers, ResultGroupingDefinition, ResultGrouping2Definition, ResultGroup } from "@common/data-models/search-results/result-group";
import { AssignmentGroupValueHelpers } from "@playbook/data-models/playbook-search-results/playbook-result-group";

import { OPTION_ALL, OPTION_BLANK, OPTION_ANY } from "@common/enums/constants";
import { AssignmentViewStateRecord } from '@suite/common/data-models/records/assignment-viewstate-record';
import { TileItem } from '../components/tile-item/tile-item';
import { INavigateEvent } from '@suite/common/interfaces/i-navigate-event';
import { NavigationDirection } from '@suite/common/enums/navigation-direction';
import { SearchViewStateRecord } from '@suite/common/data-models/records/search-viewstate-record';
import { HeaderDirective } from '@suite/common/directives/header-directive';
import { GenericAttributeDirective } from '@suite/common/directives/generic-attribute.directive';
import { TopicItemCondensed } from '@common/components/topic-item-condensed/topic-item-condensed';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { ClipboardSourceChooserComponent } from '@suite/platform/components/clipboard/modal/clipboard-source-chooser-modal.component';
import { ExportFormatRecord } from '@suite/common/data-models/records/export-format-record';
import { DateRange } from '@common/data-models/complex-types/date-range';
import { MatDialogRef } from '@angular/material/dialog';
import { StFullScreenDialog } from '@src/suite/common/st-full-screen-dialog/st-full-screen-dialog';
import { Dialog } from '@angular/cdk/dialog';
import { DateTimeRange } from '@src/suite/common/controls/date-time-range/date-time-range';


@Injectable({ providedIn: 'root' })
export class PlaybookService implements IPlaybookService {
    public TopicRequiredFieldDefs: Array<FieldDef> = [];
    public TopicRequiredFieldDefsDict: { [id: string]: FieldDef } = {};

    public AssignmentBaseRequiredFieldDefs: Array<FieldDef> = [];
    public AssignmentBaseRequiredFieldDefsDict: { [id: string]: FieldDef } = {};
    public AssignmentRequiredFieldDefs: Array<FieldDef> = [];
    public AssignmentRequiredFieldDefsDict: { [id: string]: FieldDef } = {};
    public AssignmentDoNotCopyFieldDefs: Array<FieldDef> = [];
    public AssignmentDoNotCopyFieldDefsDict: { [id: string]: FieldDef } = {};

    public get topicItems(): Array<TopicRecord> {
        return this.playbookSearchResults.topics;
    }

    //hovered over assignment
    public currentlyHoveredAssignmentGuid: string = null;

    constructor(
        public appService: AppService,
        public topicService: TopicService,
        public playbookSearchService: PlaybookSearchService,
        public navigationService: NavigationService,
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public ngZone: NgZone,
        public slideshowService: SlideshowService,
        public viewStateService: LayoutState,
        public fieldMappingService: FieldMappingService,
        private dialogService: Dialog,
    ) {
        AssignmentRecord.playbookService = this;
        TopicRecord.playbookService = this;
        PlaybookSearchResults.playbookService = this;

        Clipboard.copyViewToClipboardHandlers["playbook"] = this.copyViewToClipBoard.bind(this);
        Clipboard.renderers["playbook"] = this.copyToClipboardRenderer.bind(this);

        this.appService.newTemplateSaveAs["playbook"] = this.saveAsTemplate.bind(this);

        this.globalTablesService.staticPropertyHandlers["TOPIC"] = this.getTopicStaticProperties.bind(this);
        this.globalTablesService.staticPropertyHandlers["ASSIGNMENT"] = this.getAssignmentStaticProperties.bind(this);

        this.globalTablesService.isPropertyForType[ObjectTypes.TOPIC] = this.isTopicProperty.bind(this);
        this.globalTablesService.isPropertyForType[ObjectTypes.ASSIGNMENT] = this.isAssignmentProperty.bind(this);

        this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.TOPIC] = this.populateDefaultValuesForTopic.bind(this);
        this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.ASSIGNMENT] = this.populateDefaultValuesForAssignment.bind(this);

        this.globalTablesService.populateCopyFromParentCacheCallbacks[ObjectTypes.ASSIGNMENT] = this.populateCopyFromParentCacheForAssignment.bind(this);

        this.globalTablesService.populateExtraFiltersCallback = this.getRelationshipFilters.bind(this);

        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.TOPIC] = this.setFieldDefsForTopics.bind(this);
        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.ASSIGNMENT] = this.setFieldDefsForAssignments.bind(this);

        this.globalTablesService.calculateDefaultGroupSort["playbook"] = this.fillGroupSortIfEmpty.bind(this);

        //This should always happen after FieldDefs are set - above two callbacks do that.
        this.globalTablesService.getGroupingOptions[ViewStateTypes.TOPIC] = this.getGroupingOptionsForTopics.bind(this);
        this.globalTablesService.getGroupingOptions[ViewStateTypes.ASSIGNMENT] = this.getGroupingOptionsForAssignments.bind(this);
        this.globalTablesService.getGroupingOptions[ViewStateTypes.SEARCH] = this.getGroupingOptionsForSearch.bind(this);

        this.globalTablesService.getSortingOptions[ViewStateTypes.TOPIC] = this.getSortingOptionsForTopics.bind(this);
        this.globalTablesService.getSortingOptions[ViewStateTypes.ASSIGNMENT] = this.getSortingOptionsForAssignments.bind(this);
        this.globalTablesService.getSortingOptions[ViewStateTypes.SEARCH] = this.getSortingOptionsForSearch.bind(this);

        this.globalTablesService.getLayoutOptions[ViewStateTypes.TOPIC] = this.getLayoutOptionsForTopic.bind(this);
        this.globalTablesService.getLayoutOptions[ViewStateTypes.ASSIGNMENT] = this.getLayoutOptionsForAssignment.bind(this);

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.SEARCH] = this.getFiltersForSearch().bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.TOPIC] = this.getFiltersForTopicOrAssignment(ViewStateTypes.TOPIC).bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.SINGLETOPIC] = this.getFiltersForTopicOrAssignment(ViewStateTypes.TOPIC).bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.ASSIGNMENT] = this.getFiltersForTopicOrAssignment(ViewStateTypes.ASSIGNMENT).bind(this);
        this.globalTablesService.clearServices["playbook"] = this.clearPlaybookService.bind(this);
    }

    public AssignmentBaseRequiredFields: Array<string> = [
        PSTFieldNames.PSTAssignmentOwner,
        PSTFieldNames.PSTAssignmentSlug,
        PSTFieldNames.PSTAssignmentCoverage,
        PSTFieldNames.PSTAssignmentCoverageType,
        PSTFieldNames.PSTAssignmentPublishingDesk,
        PSTFieldNames.PSTAssignmentStatus
    ];

    public TopicBaseRequiredFields: Array<string> = [
        PSTFieldNames.PSTTopicSlug,
        PSTFieldNames.PSTTopicSummary,
        PSTFieldNames.PSTTopicDepartment,
        PSTFieldNames.PSTTopicTag,
        PSTFieldNames.PSTTopicStatus,
        PSTFieldNames.PSTTopicType,
        PSTFieldNames.PSTTopicStoryOwner,
        PSTFieldNames.Owner,
        PSTFieldNames.PSTApproved
    ];

    public AssignmentRequiredFields: Array<string> = [];

    public getTopicStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        ret[PSTFieldNames.PSTTopicSlug] = true;
        ret[PSTFieldNames.PSTTopicSummary] = true;
        ret[PSTFieldNames.PSTTopicDepartment] = true;
        ret[PSTFieldNames.PSTTopicTag] = true;
        ret[PSTFieldNames.PSTTopicStatus] = true;
        ret[PSTFieldNames.PSTTopicType] = true;
        ret[PSTFieldNames.PSTTopicStoryOwner] = true;
        ret[PSTFieldNames.Owner] = true;
        ret[PSTFieldNames.PSTApproved] = true;
        ret[PSTFieldNames.PSTTopicOrder] = true;
        return ret;
    }

    public getAssignmentStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        //ret[PSTFieldNames.PSTAssignmentOwner] = true;
        ret[PSTFieldNames.PSTAssignmentSlug] = true;
        ret[PSTFieldNames.PSTAssignmentPublishingDesk] = true;
        ret[PSTFieldNames.PSTAssignmentCoverageType] = true;
        ret[PSTFieldNames.PSTAssignmentCost] = true;
        ret[PSTFieldNames.PSTTopicTag] = true;
        ret[PSTFieldNames.PSTAssignmentStatus] = true;
        ret[PSTFieldNames.PSTAssignmentCoverage] = true;
        ret[PSTFieldNames.PSTApproved] = true;
        return ret;
    }

    public isTopicProperty(fieldDef: FieldDef): boolean {
        return fieldDef.TopicProp;
    }

    public isAssignmentProperty(fieldDef: FieldDef): boolean {
        return fieldDef.AssignmentProp;
    }

    public populateDefaultValuesForTopic(f: FieldDef, criteriaObj: any) {
        if (f.TopicProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC][f.ID] = criteriaObj;
        }
    }

    public populateDefaultValuesForAssignment(f: FieldDef, criteriaObj: any) {
        if (f.AssignmentProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT][f.ID] = criteriaObj;
        }
    }

    public populateCopyFromParentCacheForAssignment(f: FieldDef) {
        if (f.CopyToChild && ((f.AssignmentProp && f.TopicProp) || (f.Type == "System"))) {
            let criteriaObj = {
                key: f.ID,
                value: "",
                validation: f.Validation.toUpperCase()
            }
            if (!this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT]) {
                this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT] = {};
            }
            this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT][f.ID] = criteriaObj;
        }
    }

    public setFieldDefsForTopics(fieldDef: FieldDef) {
        let validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation == "CHECKBOX"
                || (validation == "LOOKUPLIST" && fieldDef.ID != PSTFieldNames.PSTTopicTag)
                || !this.globalTablesService.isValidationTypeImplemented(validation)
            ) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.TopicProp || this.TopicBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                this.TopicRequiredFieldDefs.push(fieldDef);
                this.TopicRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
            }
        }
        if (fieldDef.TopicProp) {
            this.calculateTopicPropComparators(fieldDef)
        }
    }

    public setFieldDefsForAssignments(fieldDef: FieldDef) {
        let validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation == "CHECKBOX"
                || (validation == "LOOKUPLIST" && fieldDef.ID != PSTFieldNames.PSTTopicTag)
                || !this.globalTablesService.isValidationTypeImplemented(validation)
            ) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.AssignmentProp || this.AssignmentBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                if (this.AssignmentBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                    this.AssignmentBaseRequiredFieldDefs.push(fieldDef);
                    this.AssignmentBaseRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
                }
                if (this.AssignmentRequiredFields.indexOf(fieldDef.ID) == -1) {
                    this.AssignmentRequiredFields.push(fieldDef.ID);
                    this.AssignmentRequiredFieldDefs.push(fieldDef);
                    this.AssignmentRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
                }
            }
        }
        if ((fieldDef.Type == "System" || fieldDef.AssignmentProp) && fieldDef.DoNotCopy) {
            this.AssignmentDoNotCopyFieldDefs.push(fieldDef);
            this.AssignmentDoNotCopyFieldDefsDict[fieldDef.ID] = fieldDef;
        }
        if (fieldDef.AssignmentProp) {
            this.calculateAssignmentPropComparators(fieldDef)
        }
    }


    private calculateTopicPropComparators(prop: FieldDef) {
        let shouldAdd: boolean = true;
        switch (prop.Validation.toLowerCase()) {
            case 'staffidlist': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StaffOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.StaffOrDefault(prop.ID, "No value")
                );
                break;
            }
            case 'text':
            case 'combobox':
            case 'tree': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StringOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.Generic(prop.ID)
                );
                break;
            }
            case 'checklistedit':
            case 'checklist': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.MultiValueOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.MultiValue(prop.ID)
                );
                break;
            }
            case 'datetimerange': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.MultiDay(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.MultiValue(prop.ID)
                );
                break;
            }
            case 'time': {
                PlaybookSearchResults.topicGroupDefs[`${prop.ID}-Time`] = new ResultGroupingDefinition(
                    GroupValueHelpers.Time(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID)
                );

                if (prop.GroupableProp) {
                    this.groupByTopics.push({ id: `${prop.ID}-Time`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByTimeLabel}` });
                    shouldAdd = false;
                }
                if (prop.SortableProp) {
                    this.sortByTopics.push({ id: `${prop.ID}-Time`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByTimeLabel}` });
                    shouldAdd = false;
                }
            }
            case 'date': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.DateTime(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID)
                );
                if (prop.GroupableProp) {
                    this.groupByTopics.push({ id: `${prop.ID}`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByDateLabel}` });
                    shouldAdd = false;
                }
                if (prop.SortableProp) {
                    this.sortByTopics.push({ id: `${prop.ID}`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByDateLabel}` });
                    shouldAdd = false;
                }
                break;
            }
            case 'viewstateorder': {
                if (prop.ID == PSTFieldNames.PSTTopicOrder) {
                    PlaybookSearchResults.topicGroupDefs[PSTFieldNames.PSTTopicOrder] = new ResultGroupingDefinition(
                        GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTTopicOrder, "No value"),
                        GroupKeyComparatorHelpers.Value(),
                        RecordComparatorHelpers.Manual()
                    );
                } else {
                    shouldAdd = false;
                }
                break;
            }
            case 'checkbox': {
                PlaybookSearchResults.topicGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.Boolean(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.Boolean(prop.ID)
                );
                break;
            }
            default:
                shouldAdd = false;
        }
        if (prop.GroupableProp && shouldAdd) {
            this.groupByTopics.push({ id: prop.ID, title: prop.DefaultCaption });
        }
        if (prop.SortableProp && shouldAdd) {
            this.sortByTopics.push({ id: prop.ID, title: prop.DefaultCaption });
        }
    }

    private calculateAssignmentPropComparators(prop: FieldDef) {
        let shouldAdd: boolean = true;
        switch (prop.Validation.toLowerCase()) {
            case 'staffidlist': {
                PlaybookSearchResults.assignmentGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StaffOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.StaffOrDefault(prop.ID, "No value", true)
                );
                break;
            }
            case 'text':
            case 'combobox':
            case 'tree': {
                PlaybookSearchResults.assignmentGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.StringOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.Generic(prop.ID, true)
                );
                break;
            }
            case 'checklistedit':
            case 'checklist': {
                PlaybookSearchResults.assignmentGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.MultiValueOrDefault(prop.ID, "No value"),
                    GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
                    RecordComparatorHelpers.MultiValue(prop.ID, true)
                );
                break;
            }
            case 'time': {
                PlaybookSearchResults.assignmentGroupDefs[`${prop.ID}-Time`] = new ResultGroupingDefinition(
                    GroupValueHelpers.Time(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID, true)
                );

                if (prop.GroupableProp) {
                    this.groupByAssignments.push({ id: `${prop.ID}-Time`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByTimeLabel}` });
                    shouldAdd = false;
                }
                if (prop.SortableProp) {
                    this.sortByAssignments.push({ id: `${prop.ID}-Time`, title: `${prop.DefaultCaption}-${this.languageSettings.PSTWebGroupByTimeLabel}` });
                    shouldAdd = false;
                }
            }
            case 'date': {
                PlaybookSearchResults.assignmentGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.DateTime(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.DateTime(prop.ID, true)
                );
                if (prop.GroupableProp) {
                    this.groupByAssignments.push({ id: `${prop.ID}`, title: `${prop.DefaultCaption}-Date` });
                    shouldAdd = false;
                }
                if (prop.SortableProp) {
                    this.sortByAssignments.push({ id: `${prop.ID}`, title: `${prop.DefaultCaption}-Date` });
                    shouldAdd = false;
                }
                break;
            }
            case 'checkbox': {
                PlaybookSearchResults.assignmentGroupDefs[prop.ID] = new ResultGroupingDefinition(
                    GroupValueHelpers.Boolean(prop.ID),
                    GroupKeyComparatorHelpers.Value(),
                    RecordComparatorHelpers.Boolean(prop.ID, true)
                );
                break;
            }
            default:
                shouldAdd = false;
        }
        if (prop.GroupableProp && shouldAdd) {
            this.groupByAssignments.push({ id: prop.ID, title: prop.DefaultCaption });
        }
        if (prop.SortableProp && shouldAdd) {
            this.sortByAssignments.push({ id: prop.ID, title: prop.DefaultCaption });
        }
    }

    private fillGroupSortIfEmpty() {
        PlaybookSearchResults.topicGroupDefs["SingleGroup"] = new ResultGroupingDefinition(
            GroupValueHelpers.SingleGroup(),
            GroupKeyComparatorHelpers.SingleGroup(),
            null
        );
        PlaybookSearchResults.assignmentGroupDefs[PSTFieldNames.PSTAssignmentModTime] = new ResultGroupingDefinition(
            GroupValueHelpers.DateTime(PSTFieldNames.PSTAssignmentModTime),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTAssignmentModTime, true)
        );
        PlaybookSearchResults.assignmentGroupDefs[PSTFieldNames.PSTAssignmentRole] = new ResultGroupingDefinition(
            GroupValueHelpers.RoleOrDefault("Assignment", "No role"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No role"),
            RecordComparatorHelpers.Generic(PSTFieldNames.PSTAssignmentRole, true)
        );
        PlaybookSearchResults.assignmentSearchGroupDef[PSTFieldNames.PSTModTime] = new ResultGroupingDefinition(
            AssignmentGroupValueHelpers.TopicMetadata(),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTModTime, true)
        );
        PlaybookSearchResults.topicGroupDefs[PSTFieldNames.PSTModTime] = new ResultGroupingDefinition(
            GroupValueHelpers.DateTime(PSTFieldNames.PSTModTime),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTModTime)
        );
        PlaybookSearchResults.assignmentGroupDefs[PSTFieldNames.PSTTopicFileDate] = new ResultGroupingDefinition(
            GroupValueHelpers.DateTime(PSTFieldNames.PSTTopicFileDate),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTTopicFileDate, true)
        );
        PlaybookSearchResults.assignmentSearchGroupDef[PSTFieldNames.PSTTopicFileDate] = new ResultGroupingDefinition(
            AssignmentGroupValueHelpers.TopicMetadata(),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTTopicFileDate, true)
        );
        PlaybookSearchResults.topicGroupDefs[PSTFieldNames.PSTTopicFileDate] = new ResultGroupingDefinition(
            GroupValueHelpers.DateTime(PSTFieldNames.PSTTopicFileDate),
            GroupKeyComparatorHelpers.Value(),
            RecordComparatorHelpers.DateTime(PSTFieldNames.PSTTopicFileDate)
        );

        PlaybookSearchResults.topicGroupDefs[PSTFieldNames.PSTTopicSlug] = new ResultGroupingDefinition(
            GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTTopicSlug, "No value"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
            RecordComparatorHelpers.Generic(PSTFieldNames.PSTTopicSlug)
        );
        PlaybookSearchResults.assignmentGroupDefs[PSTFieldNames.PSTTopicSlug] = new ResultGroupingDefinition(
            GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTTopicSlug, "No value"),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
            RecordComparatorHelpers.Generic(PSTFieldNames.PSTTopicSlug, true)
        );
        PlaybookSearchResults.assignmentSearchGroupDef[PSTFieldNames.PSTTopicSlug] = new ResultGroupingDefinition(
            AssignmentGroupValueHelpers.TopicMetadata(),
            GroupKeyComparatorHelpers.DisplayValueDefaultBottom("No value"),
            RecordComparatorHelpers.Generic(PSTFieldNames.PSTTopicSlug, true)
        );

        if (this.groupByTopics && this.groupByTopics.length == 0) {
            this.groupByTopics = [
                { id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption },
                { id: PSTFieldNames.PSTTopicStoryOwner, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicStoryOwner].DefaultCaption },
                { id: PSTFieldNames.PSTTopicStatus, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicStatus].DefaultCaption },
                { id: PSTFieldNames.PSTTopicDepartment, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicDepartment].DefaultCaption }
            ];
        }
        if (!this.groupByTopics.some(item => { return item.id == PSTFieldNames.PSTTopicFileDate })) this.groupByTopics.push({ id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption })

        if (this.sortByTopics && this.sortByTopics.length == 0) {
            this.sortByTopics = [
                { id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption },
                { id: PSTFieldNames.PSTTopicStoryOwner, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicStoryOwner].DefaultCaption },
                { id: PSTFieldNames.PSTTopicSlug, title: this.languageSettings.PSTWebSlugLabel },
                { id: PSTFieldNames.PSTTopicSummary, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicSummary].DefaultCaption },
                { id: PSTFieldNames.PSTTopicStatus, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicStatus].DefaultCaption },
                { id: PSTFieldNames.PSTTopicDepartment, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicDepartment].DefaultCaption },
                { id: PSTFieldNames.PSTTopicType, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicType].DefaultCaption },
            ];
        }
        if (!this.sortByTopics.some(item => { return item.id == PSTFieldNames.PSTTopicFileDate })) this.sortByTopics.push({ id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption })
        if (!this.sortByTopics.some(item => { return item.id == PSTFieldNames.PSTTopicSlug })) this.sortByTopics.push({ id: PSTFieldNames.PSTTopicSlug, title: this.languageSettings.PSTWebSlugLabel });

        if (!this.sortByTopics.some(item => { return item.id == PSTFieldNames.PSTModTime })) this.sortByTopics.unshift({ id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebSortByModTimeLabel });


        if (this.groupByAssignments && this.groupByAssignments.length == 0) {
            this.groupByAssignments = [
                { id: PSTFieldNames.PSTAssignmentFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentFileDate].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentPublishingDesk, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentPublishingDesk].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentOwner, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentOwner].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentStatus, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentStatus].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentRole, title: this.languageSettings.PSTWebGroupByRoleLabel },
            ];
        }
        if (!this.groupByAssignments.some(item => { return item.id == PSTFieldNames.PSTTopicFileDate })) this.groupByAssignments.push({ id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption })
        if (!this.groupByAssignments.some(item => { return item.id == PSTFieldNames.PSTAssignmentRole })) this.groupByAssignments.push({ id: PSTFieldNames.PSTAssignmentRole, title: this.languageSettings.PSTWebGroupByRoleLabel })

        if (this.sortByAssignments && this.sortByAssignments.length == 0) {
            this.sortByAssignments = [
                { id: PSTFieldNames.PSTAssignmentFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentFileDate].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentOwner, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentOwner].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentSlug, title: this.languageSettings.PSTWebSlugLabel },
                { id: PSTFieldNames.PSTAssignmentPublishingDesk, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentPublishingDesk].DefaultCaption },
                { id: PSTFieldNames.PSTAssignmentStatus, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTAssignmentStatus].DefaultCaption }
            ];
        }
        if (!this.sortByAssignments.some(item => { return item.id == PSTFieldNames.PSTTopicFileDate })) this.sortByAssignments.push({ id: PSTFieldNames.PSTTopicFileDate, title: this.globalTablesService.FieldDefsDict[PSTFieldNames.PSTTopicFileDate].DefaultCaption })
        if (!this.sortByAssignments.some(item => { return item.id == PSTFieldNames.PSTTopicSlug })) this.sortByAssignments.push({ id: PSTFieldNames.PSTTopicSlug, title: this.languageSettings.PSTWebSlugLabel });
        if (!this.sortByAssignments.some(item => { return item.id == PSTFieldNames.PSTAssignmentModTime })) this.sortByAssignments.unshift({ id: PSTFieldNames.PSTAssignmentModTime, title: this.languageSettings.PSTWebSortByModTimeLabel });
        this.sortBySearch = this.sortByTopics.filter(item => this.sortByAssignments.some(item2 => item.id == item2.id));
    }

    public groupByAssignments: Array<{ id: string, title: string }> = [];
    public sortByAssignments: Array<{ id: string, title: string }> = [];

    private _groupByAssignmentKeys: Array<string> = [];
    public get groupByAssignmentKeys(): Array<string> {
        if ((this._groupByAssignmentKeys.length == 0) && (this.groupByAssignments.length > 0)) {
            this._groupByAssignmentKeys = this.groupByAssignments.map(item => item.id);
        }
        return this._groupByAssignmentKeys;
    }
    public groupByTopics: Array<{ id: string, title: string }> = [];
    public sortByTopics: Array<{ id: string, title: string }> = [];

    private _groupByTopicKeys: Array<string> = [];
    public get groupByTopicKeys(): Array<string> {
        if ((this._groupByTopicKeys.length == 0) && (this.groupByTopics.length > 0)) {
            this._groupByTopicKeys = this.groupByTopics.map(item => item.id);
        }
        return this._groupByTopicKeys;
    }

    public sortBySearch: Array<{ id: string, title: string }> = [];

    public getGroupingOptionsForTopics(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTTopicFileDate;
        ret.Options = this.groupByTopics;
        return ret;
    }

    public getGroupingOptionsForAssignments(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTAssignmentFileDate;
        ret.Options = this.groupByAssignments;
        return ret;
    }

    public getGroupingOptionsForSearch(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.Type;
        ret.Options = [
            { id: PSTFieldNames.Type, title: PSTFieldNames.Type }
        ];
        return ret;
    }

    public getSortingOptionsForTopics(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTTopicFileDate;
        ret.Options = this.sortByTopics;
        return ret;
    }

    public getSortingOptionsForAssignments(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTAssignmentFileDate;
        ret.Options = this.sortByAssignments;
        return ret;
    }

    public getSortingOptionsForSearch(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTAssignmentFileDate;
        ret.Options = this.sortBySearch;
        return ret;
    }

    public getLayoutOptionsForTopic(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.CARD;
        ret.Options = [
            { id: ViewStateLayouts.CARD, title: this.languageSettings.PSTWebViewCardLabel },
            { id: ViewStateLayouts.CONDENSE, title: this.languageSettings.PSTWebViewCondensedLabel },
            { id: ViewStateLayouts.TILE, title: this.languageSettings.PSTWebViewTimelineLabel }
        ];
        if (this.globalTablesService.securityPrivileges[this.nomWebServicesJson.PSTGroup].viewUserLocationPrivileges) {
            ret.Add({ id: ViewStateLayouts.MAP, title: this.languageSettings.PSTWebViewMapLabel });
        }
        return ret;
    }

    public getLayoutOptionsForAssignment(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.CARD;
        ret.Options = [
            { id: ViewStateLayouts.CARD, title: this.languageSettings.PSTWebViewCardLabel },
            { id: ViewStateLayouts.CONDENSE, title: this.languageSettings.PSTWebViewCondensedLabel }
        ];
        return ret;
    }

    public getFiltersForSearch(): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item["TopicProp"] == true || item["Assignment"] == true)) {
                return true;
            }
            return false;
        }
    }

    public getFiltersForTopicOrAssignment(type: number): (item: FieldDef) => boolean {
        let t = "";
        if (type == ViewStateTypes.TOPIC) { t = "Topic"; }
        if (type == ViewStateTypes.ASSIGNMENT) { t = "Assignment"; }
        return (item: FieldDef) => {
            if (item.Briefable && (item[t + "Prop"] == true)) {
                return true;
            }
            return false;
        }
    }

    public copyViewToClipBoard() {
        if (this.playbookSearchResults.objectType == ObjectTypes._SEARCH) {
            this.appService.clipboard.copy(this.playbookSearchResults, ClipboardTypes.SearchView);
        } else if (this.playbookSearchResults.objectType == ObjectTypes.TOPIC) {
            this.appService.clipboard.copy(this.playbookSearchResults, ClipboardTypes.TopicView);
        } else if (this.playbookSearchResults.objectType == ObjectTypes.ASSIGNMENT) {
            this.appService.clipboard.copy(this.playbookSearchResults, ClipboardTypes.AssignmentView);
        }
    }

    private async copyToClipboardRenderer(data: any, type: ClipboardTypes): Promise<string> {
        let selectedTemplateSource: { [id: string]: ExportFormatRecord } = null
        if (this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[type] > 1) {
            let modalRes = await this.dialogService.open<{ [id: string]: ExportFormatRecord }>(ClipboardSourceChooserComponent, {
                hasBackdrop: true,
                data: {
                    type: type
                }
            }).closed.toPromise();
            if (modalRes) {
                selectedTemplateSource = modalRes;
            }
        } else {
            if (this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates[type]) {
                selectedTemplateSource = this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates;
            } else if (this.nomWebServicesJson.HelloResults.TeamClipboardTemplates[type]) {
                selectedTemplateSource = this.nomWebServicesJson.HelloResults.TeamExportFormatTemplates;
            } else {
                selectedTemplateSource = this.nomWebServicesJson.HelloResults.EnterpriseExportFormatTemplates;
            }
        }
        switch (type) {
            case ClipboardTypes.Topic:
            case ClipboardTypes.Assignment: {
                if (selectedTemplateSource) {
                    let templateStr = selectedTemplateSource[type].exportFormatTemplate
                    if (templateStr) {
                        let template = Handlebars.compile(templateStr)
                        return template(
                            data as Record,
                            {
                                allowProtoMethodsByDefault: true
                            }
                        );
                    } else {
                        return "";
                    }
                }
            }
            case ClipboardTypes.TopicView: {
                if (selectedTemplateSource) {
                    let templateStr = selectedTemplateSource[type].exportFormatTemplate
                    if (templateStr) {
                        let template = Handlebars.compile(templateStr)
                        let inputData = {
                            topics: [...PlaybookSearchResults.newlyAddedItems.items, ...this.getClipboardTopics(data)],
                        }
                        return template(
                            inputData,
                            {
                                allowProtoMethodsByDefault: true
                            }
                        );
                    } else {
                        return "";
                    }
                }
            }
            case ClipboardTypes.AssignmentView: {
                if (selectedTemplateSource) {
                    let templateStr = selectedTemplateSource[type].exportFormatTemplate
                    if (templateStr) {
                        let template = Handlebars.compile(templateStr)
                        let inputData = {
                            topics: PlaybookSearchResults.newlyAddedItems.items,
                            assignments: this.getClipboardAssignments(data)
                        }
                        return template(inputData);
                    } else {
                        return "";
                    }
                }
            }
            case ClipboardTypes.SearchView: {
                if (selectedTemplateSource) {
                    let templateStr = selectedTemplateSource[type].exportFormatTemplate
                    if (templateStr) {
                        let template = Handlebars.compile(templateStr)
                        let inputData = {
                            topics: [...PlaybookSearchResults.newlyAddedItems.items, ...this.getClipboardTopics(data)],
                            assignments: this.getClipboardAssignments(data)
                        }
                        return template(
                            inputData,
                            {
                                allowProtoMethodsByDefault: true
                            }
                        );
                    } else {
                        return "";
                    }
                }
            }
            case ClipboardTypes.TopicUrl: {
                return `${location.origin}${location.pathname}#/PST/playbook/system/topic/${(<Record>data).guid}`
            }
            case ClipboardTypes.AssignmentUrl: {
                return `${location.origin}${location.pathname}#/PST/playbook/assignment/${(<Record>data).database}/${encodeURIComponent((<Record>data).path.fullPath)}/${(<Record>data).guid}`
            }
            default:
                throw "copyToClipboardRenderer() not implemented for playbook clipboard type " + type;
        }
    }

    private getClipboardTopics(data: PlaybookSearchResults) {
        let topics: Array<Record> = [];
        if (data.objectType == ObjectTypes.TOPIC) {
            for (let group of data.topicGroups[PlaybookSearchResults.groupByField].keys) {
                for (let topic of data.topicGroups[PlaybookSearchResults.groupByField].entries[group.value]) {
                    topics.push(topic)
                }
            }
        } else {
            for (let group of data.topicSearchGroup.keys) {
                for (let topic of data.topicSearchGroup.entries[group.value]) {
                    topics.push(topic)
                }
            }
        }

        return topics;
    }

    private getClipboardAssignments(data: PlaybookSearchResults) {
        let assignments: Array<Record> = [];
        if (data.objectType == ObjectTypes.ASSIGNMENT) {
            for (let group of data.assignmentGroups[PlaybookSearchResults.groupByField].keys) {
                for (let assignment of data.assignmentGroups[PlaybookSearchResults.groupByField].entries[group.value]) {
                    assignments.push(assignment)
                }
            }
        } else {
            for (let topic of data.assignmentSearchGroup.keys) {
                for (let assignment of data.assignmentSearchGroup.entries[topic.value]) {
                    assignments.push(assignment)
                }
            }
        }

        return assignments;
    }

    public get playbookSearchResults(): PlaybookSearchResults {
        return DataStore.getSearchResults<PlaybookSearchResults>("playbook");
    }

    public activeCollectionPollerStarted: boolean = false;

    public startActiveCollectionPoller() {
        this.ngZone.runOutsideAngular(async () => {
            setInterval(async () => {
                if (!this.nomWebServicesJson.noSession) {
                    let vs: ViewStateRecordsAlias = this.viewStateService.ViewStateRecord;
                    if (vs instanceof SingleViewStateRecord) {
                        let res = await this.playbookSearchService.getFilteredResultsGuids(vs);
                        if (res.status) {
                            DataStore.getSearchResults('playbook').updateNewGuids(res.data, PlaybookSearchResults.newlyAddedItems.itemsGuids);
                        } else {
                            this.nomWebServicesJson.noSession = true;
                        }
                    }
                }
            }, this.appService.getSolrPollInvervalInSeconds());
        });
    }

    public async loadTopics(vs?: ViewStateRecordsAlias, newItems?: boolean, guid?: string) {
        if (!vs) vs = this.viewStateService.ViewStateRecord;
        if (!newItems || (vs instanceof AssignmentViewStateRecord)) {
            if (this.navigationService.currentListView) this.navigationService.currentListView.showLoader();
            await this.appService.ngZone.runOutsideAngular(async () => {
                this.appService.LoadingDataSource.next(true);
                PlaybookSearchResults.newlyAddedItems.dispose();
                let res: ServiceResponse<SearchResults> = await this.playbookSearchService.getSearchResults(vs, guid);
                if (!this.activeCollectionPollerStarted) {
                    this.activeCollectionPollerStarted = true;
                    this.startActiveCollectionPoller();
                }

                if (res.status) {
                    DataStore.registerSearchResults("playbook", res.data);
                    this.playbookSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
                } else {
                    // Error
                }
                this.appService.LoadingDataSource.next(false);
                this.navigationService.currentListView.hideLoader();
                this.appService.timeoutQueue.setTimeout(async () => {
                    this.appService.ngZone.run(() => {
                        this.navigationService.currentListChangeDetectorRef.detectChanges();
                    });
                    if (!this.navigationService.CurrentNavigationItem || !this.navigationService.CurrentNavigationItem.isNewView()) {
                        this.navigationService.currentListView.hideLoader();
                    }

                    this.appService.ngZone.run(() => {
                        this.navigationService.currentListChangeDetectorRef.detectChanges();
                    });

                    if (this.viewStateService.inSlideshowMode) {
                        this.appService.timeoutQueue.setTimeout(() => {
                            this.slideshowService.process();
                        }, 0);
                    }

                    if (this.navigationService.CurrentNavigationItem && this.navigationService.CurrentNavigationItem.isNewView()) {
                        await this.navigationService.saveCurrentNavigationItem();
                        this.navigationService.currentListView.hideLoaderDirectly();
                    }
                }, 0);

            });
        } else {
            let newItemsGuids: Array<string> = Object.keys(DataStore.getSearchResults('playbook').newGuids);
            let newTopics: Array<TopicRecord> = await this.playbookSearchService.getNewItems(newItemsGuids);
            for (let topic of newTopics) {
                this.playbookSearchResults.insertAfterTopic(
                    PlaybookSearchResults.newlyAddedItems.items[PlaybookSearchResults.newlyAddedItems.items.length - 1] as TopicRecord,
                    topic,
                    true
                )
            }

            this.appService.ngZone.run(() => {
                this.navigationService.currentListChangeDetectorRef.detectChanges();
            });
        }
    }

    public groupTopicTemplates: Array<TopicTemplateRecord> = [];

    public personalTopicTemplates: Array<TopicTemplateRecord> = [];

    public async loadTopicTemplates(vs: ViewStateRecordsAlias) {
        await this.appService.ngZone.runOutsideAngular(async () => {
            this.personalTopicTemplates = [];
            this.groupTopicTemplates = [];
            let loadPersonalTopicTemplates: boolean = true;
            let playbookTeam = this.globalTablesService.PlaybookTeamsArray.find(x => x.ID == this.appService.nomWebServicesJson.HelloResults.HomeProgram);
            let groupTemplatesPath = playbookTeam ? playbookTeam.ID : this.globalTablesService.DefaultPlaybookTeam.ID;
            let personalTemplatesPath = "U_" + this.nomWebServicesJson.UserId + "\\L";

            if (this.navigationService.inEditTeamSettingsMode && this.navigationService.currentPlaybookTeam && this.navigationService.currentPlaybookTeam != playbookTeam.ID) {
                groupTemplatesPath = this.navigationService.currentPlaybookTeam;
                loadPersonalTopicTemplates = false;
            }
            let resTemplates: ServiceResponse<PlaybookSearchResults> = await this.playbookSearchService.getTemplateSearchResults(vs, groupTemplatesPath);
            if (resTemplates.status) {
                this.groupTopicTemplates = resTemplates.data.templates.filter(t => t.path.fullPath != personalTemplatesPath);
                if (loadPersonalTopicTemplates) { //do not load personal templates if different from the home team is selected
                    this.personalTopicTemplates = resTemplates.data.templates.filter(t => t.path.fullPath == "U_" + this.nomWebServicesJson.UserId + "\\L");
                }
            }
        });
    }

    public async saveNewTopic(data: TopicRecord | TopicTemplateRecord, referalId: string, assignments: Array<AssignmentRecord>, additionalParams?: { [id: string]: any }): Promise<ServiceResponse<TopicRecord>> {
        let dataToSend = {};
        if (!data.status) {
            data.status = '';
        }
        data.type = 137;

        for (let p of data.keys) {
            if (data instanceof TopicTemplateRecord) {
                if ([
                    "MODTIME", "MODBY", "EXPIRES", "CREATEDDATETIME", "SOURCEGUID", "GUID", "PATH", "PHYSICALPATH", "VIRTUALPATH",
                    "ENPSDATABASE", "CONTAINERTITLE", "PRIORITY", "APPROVED", "OUTPUTTYPE", "CREATOR", "MOSOBJID", "MOSCONTROL",
                    "OWNER", "FILESTORAGEVERSION", "DATABASE"
                ].indexOf(p.toUpperCase()) >= 0) continue;
            } else if (data instanceof TopicRecord) {
                if (["MODTIME"].indexOf(p.toUpperCase()) >= 0) continue; //maybe we will add props in the future.
            }

            let value = data.get(p);
            if (value instanceof DateTime) {
                if (value.get()) dataToSend[p] = value.get();
            } else if (value instanceof DateRange) {
                dataToSend[p] = DateTimeRangeSerializer.Instance().serialize(value);
            } else if (value instanceof LookupList) {
                dataToSend[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (value instanceof Address) {
                dataToSend[p] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (Array.isArray(value)) {
                if (this.globalTablesService.isStaffIDListFieldType(p)) {
                    dataToSend[p] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, p);
                } else if (this.globalTablesService.isCheckListFieldType(p)) {
                    dataToSend[p] = ChecklistSerializer.Instance.serialize(value);
                } else if (this.globalTablesService.isLookupListFieldType(p)) {
                    dataToSend[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value as LookupList);
                }
            } else if (value instanceof Table) {
                dataToSend[p] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (typeof value == 'boolean') {
                if (value) {
                    dataToSend[p] = true;
                } else {
                    dataToSend[p] = false;
                }
            } else if (data.get(p) !== undefined) {
                dataToSend[p] = data.get(p);
            }
        }
        // add assignments to the dataToSend, if any
        let children = [];
        if (assignments && assignments.length > 0) {

            // process assignments
            for (let i = 0; i < assignments.length; i++) {
                let assignmentObject: any = {};
                for (let p of assignments[i].keys) {
                    if ([
                        "MODTIME", "MODBY", "EXPIRES", "CREATEDDATETIME", "SOURCEGUID", "GUID", "PATH", "PHYSICALPATH", "VIRTUALPATH",
                        "ENPSDATABASE", "CONTAINERTITLE", "PRIORITY", "APPROVED", "OUTPUTTYPE", "CREATOR", "MOSOBJID", "MOSCONTROL",
                        "OWNER", "FILESTORAGEVERSION", "DATABASE"
                    ].indexOf(p.toUpperCase()) >= 0) continue;
                    let value = assignments[i].get(p);
                    if (value instanceof DateTime) {
                        if (value.get()) assignmentObject[p] = value.get();
                    } else if (value instanceof DateRange) {
                        assignmentObject[p] = DateTimeRangeSerializer.Instance().serialize(value);
                    } else if (Array.isArray(value)) {
                        if (this.globalTablesService.isStaffIDListFieldType(p)) {
                            assignmentObject[p] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, p);
                        } else if (this.globalTablesService.isCheckListFieldType(p)) {
                            assignmentObject[p] = ChecklistSerializer.Instance.serialize(value);
                        } else if (this.globalTablesService.isLookupListFieldType(p)) {
                            assignmentObject[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value as LookupList);
                        }
                    } else if (value instanceof Table) {
                        if (value && value.data && (value.data.length > 0)) {
                            assignmentObject[p] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
                        }
                        //} else if (typeof value == 'boolean') {
                        //  if (!assignmentObject[p]) assignmentObject[p] = false;
                    } else if (value instanceof Address) {
                        assignmentObject[p] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
                    } else if (value instanceof LookupList) {
                        assignmentObject[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
                    } else if (assignments[i].get(p) !== undefined) {
                        assignmentObject[p] = assignments[i].get(p);
                    }
                }
                children.push(assignmentObject);
            }
        }
        let res = await this.topicService.createTopicContent(this.nomWebServicesJson.PSTServer, "P_" + this.nomWebServicesJson.PSTGroup + "\\W\\" + this.nomWebServicesJson.PSTHoldFolder, null, null, false, dataToSend, children, referalId, null, additionalParams);
        return res;
    }

    public async saveAsTemplate(database: string, fullPath: string, guid: string, groupId: string, refresh: boolean, referalId: string, oldRecord: Record, isNew?: boolean, mobile?: boolean): Promise<ServiceResponse<Record>> {
        if (oldRecord instanceof TopicRecord) {
            let res = await this.topicService.createTopicContent(database, fullPath, guid, groupId, false, null, null, referalId, oldRecord.title);
            if (res.data.path.fullPath == "P_" + this.nomWebServicesJson.PSTGroup + "\\L") {
                this.groupTopicTemplates.push(<TopicTemplateRecord>res.data)
            } else {
                this.personalTopicTemplates.push(<TopicTemplateRecord>res.data)
            }
            return res;
        } else {
            throw "AppService.saveAsTemplate() not implemented for objectType " + oldRecord.type;
        }
    }

    public async initializeNewTopicRecord(record: TopicRecord, viewState?: ViewStateRecord) {

        let defaultObjectPropertiesArr: Array<{ FieldName: string, FieldValue: any }> = [];
        // Initialize story owner
        record.storyOwner.set(this.nomWebServicesJson.UserId);
        record.fileDate = new DateTime();
        record.fileDate.set(new Date());


        // Initialize topic type
        let viewStateFilter = null;
        if (viewState) {
            viewStateFilter = (<TopicViewStateRecord>viewState).viewStateFilter.map;
        } else {
            viewStateFilter = (<TopicViewStateRecord>this.viewStateService.ViewStateRecord)?.viewStateFilter?.map;
        }

        let defaulTopicType = new MultiValue<string>();
        if (viewStateFilter[PSTFieldNames.PSTTopicType] && viewStateFilter[PSTFieldNames.PSTTopicType].Value != OPTION_ALL) {
            for (let val of viewStateFilter[PSTFieldNames.PSTTopicType].Value) {
                defaulTopicType.set(val);
            }
            record.set(PSTFieldNames.PSTTopicType, defaulTopicType);
        }

        for (let fieldID in this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC]) {
            let defaultValue = this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC][fieldID];
            if (defaultValue && defaultValue.value) {
                if (defaultValue.validation.toUpperCase() == "CHECKBOX") {
                    defaultValue.value = parseInt(defaultValue.value);
                }
                defaultObjectPropertiesArr.push({ FieldName: defaultValue.key, FieldValue: defaultValue.value });
            }
        }
        if (defaultObjectPropertiesArr.length > 0) {
            let defaultObjectProperties = new ObjectProperties(defaultObjectPropertiesArr);
            this.appService.nomWebServicesJson.Convertor.deserialize(record, defaultObjectProperties);
        }
    }

    public async initializeNewAssignmentRecord(assignmentRecord: AssignmentRecord, topicRecord: TopicRecord, assignmentType: string) {
        assignmentRecord.set(PSTFieldNames.PSTAssignmentSlug, topicRecord.topicSlug);

        assignmentRecord.set(PSTFieldNames.PSTAssignmentCoverage, '');
        assignmentRecord.set(PSTFieldNames.PSTAssignmentCoverageType, assignmentType);

        let defaultObjectPropertiesArr: Array<{ FieldName: string, FieldValue: any }> = [];
        for (let fieldID in this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT]) {
            let defaultValue = this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT][fieldID];
            if (defaultValue && defaultValue.value) {
                if (defaultValue.validation.toUpperCase() == "CHECKBOX") {
                    defaultValue.value = parseInt(defaultValue.value);
                }
                defaultObjectPropertiesArr.push({ FieldName: defaultValue.key, FieldValue: defaultValue.value });
            }
        }
        if (defaultObjectPropertiesArr.length > 0) {
            let defaultObjectProperties = new ObjectProperties(defaultObjectPropertiesArr);
            this.appService.nomWebServicesJson.Convertor.deserialize(assignmentRecord, defaultObjectProperties);
        }
        for (let fieldID in this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT]) {
            if (topicRecord.get(fieldID)) {
                if (topicRecord.get(fieldID).deepCopy) {
                    assignmentRecord.set(fieldID, topicRecord.get(fieldID).deepCopy());
                } else {
                    assignmentRecord.set(fieldID, topicRecord.get(fieldID));
                }
            }
        }
        this.fieldMappingService.copyToChild(topicRecord, assignmentRecord);
    }

    public hasMultipleOwners(assignment: AssignmentRecord): boolean {
        return !!assignment.storyOwner.length;
    }

    public serializeAssignmentData(data: Record): { [id: string]: any } {
        let serializedData = {};
        for (let p of data.keys) {
            let value = data.get(p);
            if (value instanceof DateTime) {
                if (value.get()) serializedData[p] = value.get();
            } else if (value instanceof LookupList) {
                serializedData[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (value instanceof Address) {
                serializedData[p] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (Array.isArray(value)) {
                if (this.globalTablesService.isStaffIDListFieldType(p)) {
                    serializedData[p] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, p);
                } else if (this.globalTablesService.isCheckListFieldType(p)) {
                    serializedData[p] = ChecklistSerializer.Instance.serialize(value);
                } else if (this.globalTablesService.isLookupListFieldType(p)) {
                    serializedData[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value as LookupList);
                }
            } else if (value instanceof Table) {
                serializedData[p] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (typeof value == 'boolean') {
                if (value) {
                    serializedData[p] = true;
                } else {
                    serializedData[p] = false;
                }
            } else if (data.get(p) !== undefined) {
                serializedData[p] = data.get(p);
            }
        }
        return serializedData;
    }

    public async duplicateItem(linkToParent: boolean, oldRecord: Record, isNew?: boolean, mobile?: boolean): Promise<ServiceResponse<TopicRecord>> {
        const database = oldRecord.database;
        const fullPath = oldRecord.path.fullPath;
        const groupId = oldRecord.path.groupId;
        const guid = oldRecord.guid
        let res = await this.topicService.createTopicContent(database, fullPath, guid, groupId, linkToParent, null, null, null);
        if (!isNew) {
            this.playbookSearchResults.insertAfterTopic(<TopicRecord>oldRecord, res.data, false);
        } else {
            this.playbookSearchResults.insertAfterTopic(<TopicRecord>oldRecord, res.data, true);
        }
        if (this.navigationService.currentListView.tileView) {
            this.navigationService.currentListView.tileView.startIndex = 0;
            this.navigationService.currentListView.tileView.pageSize = 7;
        }
        this.navigationService.currentListChangeDetectorRef.detectChanges();
        if (!mobile) {
            if (this.viewStateService.NavigationViewStateRecord instanceof TopicViewStateRecord) {
                if (this.viewStateService.NavigationViewStateRecord.viewStateLayout.Value == ViewStateLayouts.TILE) {
                    (<QueryList<any>>this.navigationService.currentListView.tileItems).forEach((item) => {
                        if (item.data.guid == res.data.guid) {
                            item.focusDuplicate();
                        }
                    });
                } else if (this.viewStateService.NavigationViewStateRecord.viewStateLayout.Value == ViewStateLayouts.MAP) {
                    if (this.navigationService.currentListView.playbookMapView) {
                        this.navigationService.currentListView.playbookMapView.refreshMapData();
                        this.navigationService.currentListView.playbookMapView.openDialog(res.data);
                    }
                } else {
                    if (this.navigationService.currentListView.cardViewRef) {
                        (<QueryList<any>>this.navigationService.currentListView.cardViewRef.topicItemsRef).forEach((item) => {
                            if (item.data.guid == res.data.guid) {
                                item.focusDuplicate();
                            }
                        });
                    }
                }
            }
        }
        return res;
    }

    public async duplicateAssignment(assignment: AssignmentRecord, topicData: TopicRecord, linkToParent: boolean) {
        let dataToSend = this.serializeAssignmentData(assignment);
        if (dataToSend[PSTFieldNames.Relationships]) {
            delete dataToSend[PSTFieldNames.Relationships];
        }
        Util.removeGeneratedFields(dataToSend, this.globalTablesService.FieldDefsDict);
        for (let f in dataToSend) {
            if (this.AssignmentDoNotCopyFieldDefsDict[f]) {
                delete dataToSend[f];
            }
        }
        let res = await this.topicService.insertIntoTopicContent(assignment.database, assignment.path.parentCollectionPath.fullPath, assignment.parentGuid, "", dataToSend, true, "", "", linkToParent);
        if (res && res.status) {
            if (topicData) {
                topicData.addChildren(res.data);
            }
            if (this.playbookSearchResults.byGuid[assignment.guid]) {
                //this.playbookSearchResults.addAssignment(res.data)
                this.playbookSearchResults.insertAfterAssignment(assignment, res.data)
            }
        }
        return res;
    }

    public async createNewAssignment(database: string, path: string, type: string, topicData: TopicRecord, referalId: string, assignmentRecord?: AssignmentRecord): Promise<ServiceResponse<AssignmentRecord>> {
        let dataToSend = {};
        let data = assignmentRecord;
        if (!data) {
            data = new AssignmentRecord();
            await this.initializeNewAssignmentRecord(data, topicData, <AssignmentCoverageType>type);
        }

        for (let p of data.keys) {
            let value = data.get(p);
            if (value instanceof DateTime) {
                if (value.get()) dataToSend[p] = value.get();
            } else if (Array.isArray(value)) {
                if (this.globalTablesService.isStaffIDListFieldType(p)) {
                    dataToSend[p] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, p);
                } else if (this.globalTablesService.isCheckListFieldType(p)) {
                    dataToSend[p] = ChecklistSerializer.Instance.serialize(value);
                } else if (this.globalTablesService.isLookupListFieldType(p)) {
                    dataToSend[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value as LookupList);
                }
            } else if (value instanceof Table) {
                if (value && value.data && (value.data.length > 0)) {
                    dataToSend[p] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
                }
            } else if (value instanceof LookupList) {
                dataToSend[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (value instanceof Address) {
                dataToSend[p] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (typeof value == 'boolean') {
                if (value) {
                    dataToSend[p] = true;
                } else {
                    dataToSend[p] = false;
                }
            } else if (data.get(p) !== undefined) {
                dataToSend[p] = data.get(p);
            }
        }

        dataToSend["CreatedBy"] = "CreatedBy." + this.nomWebServicesJson.UserId
        let res = await this.topicService.insertIntoTopicContent(database, topicData.path.fullPath, topicData.guid, "", dataToSend, true, "", referalId, true);
        return res;
    }

    // update single record in the timeline (when adding a topic, so no need to readjust the whole timeline )
    public updateTimelineRecord(rec: TopicRecord) {

    }

    public async copyENPSObject(sourceListData: ListData, referal: string): Promise<ServiceResponse<TopicRecord>> {
        let destinationDatabase: string = this.nomWebServicesJson.PSTServer;
        let destinationPath: string = "P_" + this.nomWebServicesJson.PSTGroup + "\\W\\" + this.nomWebServicesJson.PSTHoldFolder;
        let targetType = ObjectTypes.TOPIC;

        let propertiesOverride = [
            {
                FieldName: PSTFieldNames.PSTTopicFileDate,
                FieldValue: new Date()
            },
            {
                FieldName: PSTFieldNames.PSTCreatedBy,
                FieldValue: "CreatedBy." + sourceListData.Creator
            }
        ];

        let res = await this.appService.nomWebServicesJson.copyENPSObject(sourceListData.ENPSDatabase, sourceListData.Path, sourceListData.Guid, sourceListData.Type, destinationDatabase, destinationPath, ObjectTypes.TOPIC, propertiesOverride, referal);
        return res;
    }

    public async copyScriptToCollection(sourceListData: ListData, targetCollection: TopicRecord, referal: string) {
        let collectionDatabase: string = this.nomWebServicesJson.PSTServer;
        let collectionPath: string = targetCollection.path.fullPath;
        let collectionGuid: string = targetCollection.guid;
        let destinationType: ObjectTypes = ObjectTypes.ASSIGNMENT;

        let propertiesOverride: Array<{ FieldName: string, FieldValue: any }> = [];
        propertiesOverride.push({ "FieldName": PSTFieldNames.PSTAssignmentCoverageType, "FieldValue": "Text" })

        let res = await this.appService.nomWebServicesJson.copyScriptToCollection(collectionDatabase, collectionPath, collectionGuid, destinationType, sourceListData.ENPSDatabase, sourceListData.Path, sourceListData.Guid, sourceListData.Type, referal, propertiesOverride);
        return res;
    }

    private _topicPropsCache: Array<FieldDef> = [];
    public getTopicFieldsList(): Array<FieldDef> {
        if (this._topicPropsCache && this._topicPropsCache.length > 0) return this._topicPropsCache;
        const groups = this.globalTablesService.getFieldGroupsFor("Topic");
        let ret = new Array<FieldDef>();
        for (let group of groups) {
            ret = [...ret, ...this.globalTablesService.getDynamicPropertiesFor("Topic", group)]
        }
        this._topicPropsCache = ret;
        return this._topicPropsCache;
    }

    public getRelationshipFilters(type: number): Array<FilterDef> {
        if (type != ViewStateTypes.TOPIC) {
            return new Array<FilterDef>();
        }
        let filters: Array<FilterDef> = [];
        let assignmentsFilter = new FilterDef();
        assignmentsFilter.ID = "Assignments";
        assignmentsFilter.IsRelationship = true;
        assignmentsFilter.PSTField = PSTFieldNames.PSTAssignmentCoverageType;
        assignmentsFilter.DefaultCaption = this.languageSettings.PSTWebAssignmentsFilterLabel;
        assignmentsFilter.Validation = "CHECKLIST";
        assignmentsFilter.Options = [
            { id: OPTION_ALL, title: this.languageSettings.PSTWebNotAppliedOption, selectable: true },
            { id: OPTION_ANY, title: this.languageSettings.PSTWebAnyOption, selectable: true },
            { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true }
        ].concat(this.globalTablesService.getListItemsForField(PSTFieldNames.PSTAssignmentCoverageType).map((item) => {
            return { id: item.id, title: this.getTitleForAssignmentCoverage(item.id), selectable: true };
        }));
        assignmentsFilter.init();
        let missingAssignmentsFilter = new FilterDef();
        missingAssignmentsFilter.IsRelationship = true;
        missingAssignmentsFilter.ID = "MissingAssignments";
        missingAssignmentsFilter.PSTField = PSTFieldNames.PSTAssignmentCoverageType;
        missingAssignmentsFilter.IsNegation = true;
        missingAssignmentsFilter.DefaultCaption = this.languageSettings.PSTWebMissingAssignmentsFilterLabel;
        missingAssignmentsFilter.Validation = "CHECKLIST";
        missingAssignmentsFilter.Options = [
            { id: OPTION_ALL, title: this.languageSettings.PSTWebNotAppliedOption, selectable: true }
        ].concat(this.globalTablesService.getListItemsForField(PSTFieldNames.PSTAssignmentCoverageType).map((item) => {
            return { id: item.id, title: this.getTitleForAssignmentCoverage(item.id), selectable: true };
        }));
        missingAssignmentsFilter.init();
        filters.push(assignmentsFilter);
        filters.push(missingAssignmentsFilter);
        return filters;
    }

    private getTitleForAssignmentCoverage(id: string): string {
        switch (id) {
            case 'Text':
                return this.languageSettings.PSTWebTextAssignmentIconTooltip;
            case 'Photo':
                return this.languageSettings.PSTWebPhotoAssignmentIconTooltip;
            case 'Video':
                return this.languageSettings.PSTWebVideoAssignmentIconTooltip;
            case 'LiveVideo':
                return this.languageSettings.PSTWebLiveVideoAssignmentIconTooltip;
            case 'Audio':
                return this.languageSettings.PSTWebAudioAssignmentIconTooltip;
            case 'Graphic':
                return this.languageSettings.PSTWebGraphicAssignmentIconTooltip;
            case 'Interactive':
                return this.languageSettings.PSTWebInteractiveAssignmentIconTooltip;
            case 'SocialPromotion':
                return this.languageSettings.PSTWebSocialPromotionAssignmentIconTooltip;
            default:
                return id;
        }
    }
    public clearPlaybookService() {
        this.groupByTopics = [];
        this.groupByAssignments = [];
        this.sortByAssignments = [];
        this.sortByTopics = [];
        this.sortBySearch = [];
    }

    public async initializeTopicFromTemplate(database: string, path: string, guid: string): Promise<TopicTemplateRecord> {

        const fieldsToSkip: { [field: string]: boolean } = {};
        fieldsToSkip[PSTFieldNames.PSTTopicSlug.toLowerCase()] = true;
        let rec = await this.topicService.getTopicTemplateContent(database, path, guid, "");
        const data = new TopicTemplateRecord();
        let dt = new DateTime();
        dt.set(new Date());
        rec.data.set(PSTFieldNames.PSTTopicFileDate, dt);
        rec.data.storyOwner = new MultiValue<string>();
        rec.data.storyOwner.set(this.nomWebServicesJson.UserId);
        rec.data.copyDataAndChildrenTo(data, false, true, fieldsToSkip);
        return data;
    }


    public navigateToTopic(event: INavigateEvent, dialogRef?: MatDialogRef<StFullScreenDialog>) {
        const treeControl = this.playbookSearchResults.treeControl;
        let newIdx = -1;
        switch (event.direction) {
            case NavigationDirection.FIRST: {
                const item = treeControl.dataNodes.find((el, idx) => {
                    if (el.level === 1) {
                        newIdx = idx;
                        return true;
                    }
                })
                dialogRef.componentInstance.openTopic(item.object as TopicRecord, item.isMetadata);
                dialogRef.componentInstance.currentIndex = newIdx;
                dialogRef.componentInstance.indexInData = 1;
                break;
            }
            case NavigationDirection.PREVIOUS: {
                let item: TreeLikeFlatNode<Record> = null;
                for (let i = treeControl.dataNodes.length - 1; i >= 0; i--) {
                    if (treeControl.dataNodes[i].level === 1 && i < event.idx) {
                        newIdx = i;
                        item = treeControl.dataNodes[i];
                        break;
                    }
                }
                if (item) {
                    dialogRef.componentInstance.openTopic(item.object as TopicRecord, item.isMetadata);
                    dialogRef.componentInstance.currentIndex = newIdx;
                    dialogRef.componentInstance.indexInData--;
                } else {
                    this.navigateToTopic({ direction: NavigationDirection.LAST, idx: 0 }, dialogRef);
                }
                break;
            }
            case NavigationDirection.NEXT: {
                const item = treeControl.dataNodes.find((el, idx) => {
                    if (el.level === 1 && idx > event.idx) {
                        newIdx = idx;
                        return true;
                    }
                });
                if (item) {
                    dialogRef.componentInstance.openTopic(item.object as TopicRecord, item.isMetadata);
                    dialogRef.componentInstance.currentIndex = newIdx;
                    dialogRef.componentInstance.indexInData++;
                } else {
                    this.navigateToTopic({ direction: NavigationDirection.FIRST, idx: 0 }, dialogRef);
                }
                break;
            }
            case NavigationDirection.LAST: {
                let item = null;
                for (let i = treeControl.dataNodes.length - 1; i >= 0; i--) {
                    if (treeControl.dataNodes[i].level === 1) {
                        newIdx = i;
                        item = treeControl.dataNodes[i];
                        break;
                    }
                }
                dialogRef.componentInstance.openTopic(item.object as TopicRecord, item.isMetadata);
                dialogRef.componentInstance.currentIndex = newIdx;
                if(item.isMetadata) {
                    dialogRef.componentInstance.indexInData = treeControl.dataNodes.filter((el) => el.level == 1).length;
                } else {
                    dialogRef.componentInstance.indexInData = treeControl.dataNodes.filter((el) => el.level > 0).length;
                }
                break;
            }
        }
    }

}
